/**
 * Rendering Engine — geometry, auto layout and constraints resolution.
 *
 * The model never authors pixels. This module creates ALL geometry: it measures
 * every node, resolves stack / row / grid / free layouts, and assigns absolute
 * x, y, width, height in canvas units.
 *
 * Two passes, as a layout engine should:
 *   measure(node, available) → the size a node wants
 *   place(node, box)         → the absolute box it gets, recursively
 *
 * Explicit geometry is honoured when a document carries it (the schema permits
 * it); everything else is resolved. Pure and deterministic — same document in,
 * identical geometry out.
 */

import { LAYOUT_DEFAULTS, DEFAULT_GRID_COLS, INTRINSIC, TYPE_SCALE, CONTAINER_KINDS } from './rendererSchema';
import { applyTextCase } from './tokens';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const asArray = (v) => (Array.isArray(v) ? v : []);
// `+ 0` normalises negative zero: scaling a zero offset produces -0, which
// compares equal to 0 but serialises into the editor document as "-0".
const round = (n) => (Math.round(n * 100) / 100) + 0;

/** Intrinsic sizes are authored in px; a mm canvas converts them. */
const intrinsic = (ctx, px) => px * (ctx.scale || 1);

/** A node's layout, with the defaults filled in. */
export const layoutOf = (node) => {
  const l = isObj(node) && isObj(node.layout) ? node.layout : {};
  return { ...LAYOUT_DEFAULTS, ...l };
};

/**
 * The children of a container.
 *
 * A group holds `children`; a frame is a shaped clipping container holding a
 * single `child`. Every other node kind is a LEAF: children on a leaf are
 * illegal and are ignored, because honouring them would parent an element to a
 * non-group and corrupt the editor's grouping model.
 */
export const childrenOf = (node) => {
  if (!isObj(node) || !CONTAINER_KINDS.includes(node.node)) return [];
  if (Array.isArray(node.children)) return node.children.filter(isObj);
  if (isObj(node.child)) return [node.child];
  return [];
};

/** Padding (`pad` in the frozen contract) may be a token, a number, or per-side. */
const resolvePadding = (pad, tokens) => {
  if (isObj(pad)) {
    return {
      top: tokens.space(pad.top, 0), right: tokens.space(pad.right, 0),
      bottom: tokens.space(pad.bottom, 0), left: tokens.space(pad.left, 0),
    };
  }
  const all = tokens.space(pad, 0);
  return { top: all, right: all, bottom: all, left: all };
};

/**
 * Average glyph advance for THIS style, as a fraction of font size.
 *
 * One flat factor is wrong at the two ends of a type scale. Body copy at 400
 * sits near the base figure; a display headline is set heavy and often upper
 * case, where glyphs are materially wider. Measuring it as if it were body text
 * made a three-line headline measure as two, and the section beneath it was
 * placed straight through the third line.
 */
const advanceFactor = (style) => {
  const weight = Number.isFinite(style.weight) ? style.weight : 400;
  const weightBoost = 1 + Math.max(0, weight - 400) / 3000; // 700 → 1.10
  const caseBoost = style.case === 'upper' ? 1.12 : 1;
  return INTRINSIC.charWidthFactor * weightBoost * caseBoost;
};

/**
 * Lines a paragraph needs under GREEDY WORD WRAP.
 *
 * Dividing character count by line capacity assumes text packs perfectly, and
 * it does not: a word never splits, so most lines end early. That undercount is
 * what let a headline overlap the content below it.
 */
const wrappedLines = (para, capacity) => {
  const words = String(para).split(/\s+/).filter(Boolean);
  if (!words.length) return 1;

  let lines = 1;
  let used = 0;
  words.forEach((word) => {
    const withWord = used === 0 ? word.length : used + 1 + word.length;
    if (withWord <= capacity) { used = withWord; return; }

    // Move to a fresh line — but only if the current one has something on it.
    if (used > 0) { lines += 1; used = 0; }

    // A word wider than the line takes ONE line and overhangs it. Browsers do
    // not break words by default, and pretending otherwise was catastrophic:
    // in a narrow column a display headline counted several lines per word, so
    // "Healthy smiles, made simple." measured a box 647px past the bottom of
    // the page while its glyphs rendered in four lines mid-canvas.
    used = word.length;
  });
  return lines;
};

/** How many lines this copy needs at this width — deterministic, no DOM. */
const estimateTextHeight = (text, style, width, scale = 1) => {
  const content = applyTextCase(text, style.case);
  const charsPerLine = Math.max(1, Math.floor(width / (style.fontSize * advanceFactor(style))));
  // Honour explicit newlines, then wrap each paragraph.
  const lines = String(content).split('\n').reduce((sum, para) => sum + wrappedLines(para, charsPerLine), 0);
  return Math.max(INTRINSIC.minTextHeight * scale, round(lines * style.fontSize * style.leading));
};

/** Primitives with a real intrinsic width; everything else is a flexible block. */
const HUGS_WIDTH = Object.freeze(['text', 'icon']);

/**
 * Primitives that hug their width down a STACK as well as across a row.
 *
 * Text is deliberately absent: a paragraph filling the column is how text
 * wraps. An icon is different — it is a mark with its own size, and measuring
 * it against the container made a real generation render 317px-wide "icons"
 * as bars across the top of every feature card.
 */
const HUGS_WIDTH_ANY_AXIS = Object.freeze(['icon']);

/** The columns a grid layout uses, from the frozen `grid` spec. */
const colsOf = (l) => Math.max(1, isObj(l.grid) && isNum(l.grid.cols) ? l.grid.cols : DEFAULT_GRID_COLS);

/**
 * How many columns each child of a grid occupies.
 *
 * SEMANTICS: `grid.cols` is a design ALIGNMENT grid — the column track children
 * snap to. It is NOT a child-distribution system, and it does not mean "this
 * many children per row".
 *
 * Design JSON V2 carries no span, so there is nothing authoritative to read.
 * With no span present the renderer FALLS BACK to an equal share of the track
 * (1 child → the full width, 2 → halves, 3 → thirds). That fallback is a
 * consequence of the missing information, not the meaning of the grid: if
 * explicit span information ever exists, it must override this entirely and
 * this function is where it takes effect.
 *
 * @returns {number[]} columns per child, index-aligned with `kids`.
 */
const columnSpans = (kids, cols) => {
  const perRow = Math.min(kids.length, cols) || 1;
  const span = Math.max(1, Math.floor(cols / perRow));
  return kids.map(() => span); // fallback only — no span is authored today
};

/** Children per grid row, given the spans each one occupies. */
const gridPerRow = (kids, cols) => Math.min(kids.length, cols) || 1;

/** A node's declared `sizing`, the frozen contract's single sizing field. */
const sizingOf = (node) => (isObj(node) && isObj(node.layout) && node.layout.sizing) || null;

/**
 * How a node sizes horizontally.
 *
 * An explicit width wins. Across a row, width is the MAIN axis, so the node's
 * declared `sizing` governs it and text and icons otherwise hug their content.
 * Down a stack, width is the CROSS axis, so a node spans the container.
 */
export const widthModeOf = (node, axis = 'vertical') => {
  if (isNum(node && node.width)) return 'fixed';
  if (axis === 'horizontal') {
    const sizing = sizingOf(node);
    if (sizing) return sizing;
    return HUGS_WIDTH.includes(node && node.type) ? 'hug' : 'fill';
  }
  if (HUGS_WIDTH_ANY_AXIS.includes(node && node.type) && !sizingOf(node)) return 'hug';
  return 'fill';
};

/**
 * How a node sizes vertically.
 *
 * Down a stack, height is the MAIN axis, so `sizing: 'fill'` claims the
 * remaining space. Across a row, height is the CROSS axis and hugs.
 */
export const heightModeOf = (node, axis = 'vertical') => {
  if (isNum(node && node.height)) return 'fixed';
  if (axis !== 'horizontal' && sizingOf(node) === 'fill') return 'fill';
  return 'hug';
};

/**
 * The size a node wants inside `available` space.
 * `available.axis` is the parent's main axis, which decides default sizing.
 * @returns {{ width: number, height: number }}
 */
export const measure = (node, available, ctx) => {
  const { tokens } = ctx;
  const avail = { width: Math.max(0, available.width), height: Math.max(0, available.height) };
  const widthMode = widthModeOf(node, available.axis);
  const heightMode = heightModeOf(node, available.axis);

  const width = widthMode === 'fixed' ? node.width
    : (widthMode === 'fill' ? avail.width : intrinsicWidth(node, avail, ctx));
  const height = heightMode === 'fixed' ? node.height
    : (heightMode === 'fill' ? avail.height : intrinsicHeight(node, width, ctx, tokens));

  return { width: round(Math.max(0, width)), height: round(Math.max(0, height)) };
};

/** Text hugs the width its copy actually needs, never more than it is given. */
const estimateTextWidth = (text, style, max) =>
  Math.min(max, Math.ceil(String(text || '').length * style.fontSize * advanceFactor(style)));

const intrinsicWidth = (node, avail, ctx) => {
  if (node.node === 'group' || node.node === 'frame') {
    const l = layoutOf(node);
    const pad = resolvePadding(l.pad, ctx.tokens);
    const kids = childrenOf(node);
    if (l.type === 'row') {
      // Hugging a row means the sum of its children plus the gaps.
      const gap = ctx.tokens.space(l.gap, 0);
      const inner = kids.reduce((sum, c) => sum + measure(c, { ...avail, axis: 'horizontal' }, ctx).width, 0) + gap * Math.max(0, kids.length - 1);
      return inner + pad.left + pad.right;
    }
    // Hugging a STACK means the widest child, not the whole container.
    //
    // Returning the container width made every hugging group as wide as the
    // space offered to it, and `sizing: 'hug'` is the schema default. Three
    // feature cards in a row therefore each measured the full column width and
    // were placed side by side at 0, 459 and 918 — the third started 918px into
    // a 1080px page and ran 829px off the right edge. It looked like a spacing
    // bug and was a measurement one.
    if (!kids.length) return avail.width;
    // The children's own INTRINSIC widths — measuring them normally would ask
    // what they do with the space offered, and text offered a column fills it.
    const widest = kids.reduce((max, c) => Math.max(max, intrinsicWidth(c, avail, ctx)), 0);
    return Math.min(avail.width, widest + pad.left + pad.right);
  }
  if (node.type === 'icon') return intrinsic(ctx, INTRINSIC.iconSize);
  if (node.type === 'text') return estimateTextWidth(node.text, ctx.tokens.typeStyle(node.style || 'body'), avail.width);
  return avail.width;
};

/**
 * The smallest share of a row a `fill` child may be reduced to.
 *
 * A hero photograph declared `sizing: 'fill'` beside a hugging copy column got
 * whatever was left over, and when the copy hugged wide that was nothing: the
 * hero measured 0% of the canvas on a real Sol flyer. A child asked to fill is
 * a participant in the row, not a remainder.
 *
 * The floor is an EQUAL share of the row, because that is what the two
 * declarations together mean: text that can wrap has no claim to more of the
 * row than the image it was placed beside. Twenty percent — a flat floor tried
 * first — kept the hero on the page but left it at 8% of the canvas, still too
 * small to carry the focal role its own document assigned it.
 */
const MIN_FILL_SHARE = 0.2;
const MAX_FILL_RESERVE = 0.5;

/** The least width one `fill` child may be squeezed to, as a share of the row. */
const fillFloor = (childCount) => Math.min(
  MAX_FILL_RESERVE,
  Math.max(MIN_FILL_SHARE, childCount > 0 ? 1 / childCount : MIN_FILL_SHARE),
);

/**
 * The widths a row's children get: intrinsic ones hug, flexible ones share the rest.
 *
 * When the hugging children want more than the row has, they are scaled down
 * proportionally rather than allowed to run past the edge. Nothing is dropped
 * and no child is hidden — text simply wraps into a narrower column, which is
 * what a narrower column means. Fixed widths are honoured as written: an
 * explicit width is a decision, not a preference.
 */
const rowWidths = (kids, inner, gap, ctx) => {
  const modes = kids.map((k) => widthModeOf(k, 'horizontal'));
  const hug = kids.map((k, i) => (modes[i] === 'fill' ? 0 : measure(k, { ...inner, axis: 'horizontal' }, ctx).width));
  const fillers = modes.filter((m) => m === 'fill').length;
  const gaps = gap * Math.max(0, kids.length - 1);

  // Room held back so `fill` children cannot be squeezed out of existence.
  // The page negotiates this floor downwards when the resulting composition
  // cannot fit the canvas — see fitSpacing — so it arrives via the context.
  const floor = Number.isFinite(ctx.fillFloor) ? ctx.fillFloor : fillFloor(kids.length);
  const reserved = Math.min(inner.width * floor * fillers, inner.width * 0.75);
  const hugTotal = hug.reduce((a, b) => a + b, 0);

  if (hugTotal + gaps + reserved > inner.width) {
    // Only elastic children give ground; a fixed width stays fixed.
    const elastic = hug.reduce((sum, w, i) => sum + (modes[i] === 'hug' ? w : 0), 0);
    const fixed = hugTotal - elastic;
    const room = Math.max(0, inner.width - gaps - reserved - fixed);
    const factor = elastic > 0 ? Math.min(1, room / elastic) : 1;
    kids.forEach((_, i) => { if (modes[i] === 'hug') hug[i] *= factor; });
  }

  const used = hug.reduce((a, b) => a + b, 0) + gaps;
  const share = fillers ? Math.max(0, (inner.width - used) / fillers) : 0;
  return modes.map((m, i) => (m === 'fill' ? share : hug[i]));
};

/**
 * A child measured as if it had never asked to fill.
 *
 * `sizing: 'fill'` means "take whatever is left". Asking such a child how tall
 * it is while offering it zero height answers zero — so a container summing its
 * children measured nothing, reported almost no height of its own, and then
 * drew its contents straight out of the bottom of that box. A real Sol hero
 * measured 480px and rendered 1232px, putting the headline through three
 * feature cards below it.
 *
 * When the question is "how much room does this need", a filler has to answer
 * with its content.
 */
const naturalSize = (child, avail, ctx) => {
  const l = isObj(child) && isObj(child.layout) ? child.layout : null;
  const node = l && l.sizing === 'fill' ? { ...child, layout: { ...l, sizing: 'hug' } } : child;
  return measure(node, avail, ctx);
};

const intrinsicHeight = (node, width, ctx, tokens) => {
  switch (node.node) {
    case 'group':
    case 'frame':
      return measureContainer(node, width, ctx).height;
    default: break;
  }
  switch (node.type) {
    case 'text': {
      const style = tokens.typeStyle(node.style || 'body');
      return estimateTextHeight(node.text, style, Math.max(1, width), ctx.scale || 1);
    }
    case 'icon': return intrinsic(ctx, INTRINSIC.iconSize);
    case 'line': return intrinsic(ctx, Math.max(INTRINSIC.lineThickness, isNum(node.stroke && node.stroke.width) ? node.stroke.width : INTRINSIC.lineThickness));
    case 'image': return round(width * INTRINSIC.imageRatio); // a ratio of an already-resolved width
    case 'shape':
    case 'vector': return intrinsic(ctx, INTRINSIC.shapeHeight);
    default: return intrinsic(ctx, INTRINSIC.shapeHeight);
  }
};

/** The height a container needs for its children at `width`. */
const measureContainer = (node, width, ctx) => {
  const l = layoutOf(node);
  const pad = resolvePadding(l.pad, ctx.tokens);
  const gap = ctx.tokens.space(l.gap, 0);
  const kids = childrenOf(node);
  const innerWidth = Math.max(0, width - pad.left - pad.right);
  if (kids.length === 0) return { width, height: pad.top + pad.bottom };

  if (l.type === 'row') {
    // Each child is measured at the width the row actually gives it.
    const widths = rowWidths(kids, { width: innerWidth, height: 0 }, gap, ctx);
    const tallest = kids.reduce((max, c, i) => Math.max(max, naturalSize(c, { width: widths[i], height: 0, axis: 'horizontal' }, ctx).height), 0);
    return { width, height: round(tallest + pad.top + pad.bottom) };
  }
  if (l.type === 'grid') {
    const cols = colsOf(l);
    const colWidth = (innerWidth - gap * (cols - 1)) / cols;
    const spans = columnSpans(kids, cols);
    const perRow = gridPerRow(kids, cols);
    // A child spans its columns AND the gaps bridged between them.
    const widthOf = (i) => spans[i] * colWidth + (spans[i] - 1) * gap;
    const rows = Math.ceil(kids.length / perRow);
    let total = pad.top + pad.bottom + gap * Math.max(0, rows - 1);
    for (let r = 0; r < rows; r += 1) {
      const row = kids.slice(r * perRow, r * perRow + perRow);
      total += row.reduce((max, c, j) => Math.max(max, naturalSize(c, { width: widthOf(r * perRow + j), height: 0 }, ctx).height), 0);
    }
    return { width, height: round(total) };
  }
  if (l.type === 'free') {
    // Free children position themselves; the container spans whatever they reach.
    const bottom = kids.reduce((max, c) => Math.max(max, (isNum(c.y) ? c.y : 0) + naturalSize(c, { width: innerWidth, height: 0 }, ctx).height), 0);
    return { width, height: round(bottom + pad.top + pad.bottom) };
  }
  // stack
  const total = kids.reduce((sum, c) => sum + naturalSize(c, { width: innerWidth, height: 0 }, ctx).height, 0);
  return { width, height: round(total + gap * Math.max(0, kids.length - 1) + pad.top + pad.bottom) };
};

/** Cross-axis offset for `align` within `extent`. */
const alignOffset = (align, extent, size) => {
  switch (align) {
    case 'center': return (extent - size) / 2;
    case 'end': return extent - size;
    default: return 0; // start / stretch / baseline
  }
};

/** Main-axis start offset and spacing for `distribute`. */
const distribute = (mode, extent, sizes, gap) => {
  const total = sizes.reduce((a, b) => a + b, 0);
  const n = sizes.length;
  const free = extent - total;

  // Content that does not fit starts at the origin and overflows FORWARD.
  //
  // Every distribution below divides `free` to find its starting offset, and
  // `free` is negative whenever the children are larger than the box. A
  // centred stack taller than its section then starts at a negative offset:
  // the first real generation put a 713px hero image in a centred hero and the
  // whole hero — background, headline, image — rendered above the canvas, while
  // the CTA it pushed down landed on top of the features section. Overflowing
  // backwards is never what a layout means.
  if (free <= 0) return { start: 0, gap };

  switch (mode) {
    case 'center': return { start: (free - gap * (n - 1)) / 2, gap };
    case 'end': return { start: free - gap * (n - 1), gap };
    case 'between': return { start: 0, gap: n > 1 ? free / (n - 1) : gap };
    case 'around': { const g = n ? free / n : 0; return { start: g / 2, gap: g }; }
    case 'evenly': { const g = free / (n + 1); return { start: g, gap: g }; }
    default: return { start: 0, gap }; // start
  }
};

/**
 * Place a node and everything under it, producing absolute geometry.
 *
 * @param {Object} node
 * @param {{x:number,y:number,width:number,height:number}} box  the box the node occupies.
 * @param {Object} ctx  { tokens }
 * @returns {Object} the node with resolved { x, y, width, height } throughout.
 */
export const place = (node, box, ctx) => {
  const placed = {
    ...node,
    x: round(box.x), y: round(box.y), width: round(box.width), height: round(box.height),
  };
  const kids = childrenOf(node);
  if (kids.length === 0) return placed;

  // A frame carries one `child`; a group carries `children`. Re-attach placed
  // kids to whichever slot the node actually uses.
  const reattach = (list) => (Array.isArray(node.children) || list.length !== 1 || !isObj(node.child)
    ? { ...placed, children: list }
    : { ...placed, child: list[0] });

  const l = layoutOf(node);
  const pad = resolvePadding(l.pad, ctx.tokens);
  const gap = ctx.tokens.space(l.gap, 0);
  const inner = {
    x: box.x + pad.left,
    y: box.y + pad.top,
    width: Math.max(0, box.width - pad.left - pad.right),
    height: Math.max(0, box.height - pad.top - pad.bottom),
  };

  if (l.type === 'free') {
    // Explicit coordinates are relative to the container.
    return reattach(
      kids.map((child) => {
        const size = measure(child, inner, ctx);
        return place(child, {
          x: inner.x + (isNum(child.x) ? child.x : 0),
          y: inner.y + (isNum(child.y) ? child.y : 0),
          width: size.width, height: size.height,
        }, ctx);
      }),
    );
  }

  if (l.type === 'grid') {
    const cols = colsOf(l);
    const colWidth = (inner.width - gap * (cols - 1)) / cols;
    const spans = columnSpans(kids, cols);
    const perRow = gridPerRow(kids, cols);
    const widthOf = (i) => spans[i] * colWidth + (spans[i] - 1) * gap;

    const rows = Math.ceil(kids.length / perRow);
    const rowHeights = [];
    for (let r = 0; r < rows; r += 1) {
      const row = kids.slice(r * perRow, r * perRow + perRow);
      rowHeights.push(row.reduce((max, c, j) => Math.max(max, naturalSize(c, { width: widthOf(r * perRow + j), height: 0 }, ctx).height), 0));
    }

    return reattach(
      kids.map((child, i) => {
        const row = Math.floor(i / perRow);
        // Children snap to column boundaries: skip the columns already consumed
        // by the earlier children in this row.
        const startCol = (i % perRow) * spans[i];
        const cell = widthOf(i);
        const size = measure(child, { width: cell, height: rowHeights[row] }, ctx);
        const top = inner.y + rowHeights.slice(0, row).reduce((a, b) => a + b, 0) + gap * row;
        return place(child, {
          x: inner.x + startCol * (colWidth + gap) + alignOffset(l.align, cell, size.width),
          y: top,
          width: l.align === 'stretch' ? cell : size.width,
          height: size.height,
        }, ctx);
      }),
    );
  }

  const horizontal = l.type === 'row';
  // Across a row, widths come from the flex distribution; down a stack each
  // child is measured against the full inner width.
  const widths = horizontal ? rowWidths(kids, { width: inner.width, height: inner.height }, gap, ctx) : null;
  // Natural sizes first. A `fill` child asked for its height against zero
  // available space answers zero, so down a stack it collapsed and its own
  // contents spilled out of the bottom of nothing.
  const sizes = kids.map((child, i) => (horizontal
    ? { width: widths[i], height: naturalSize(child, { width: widths[i], height: inner.height, axis: 'horizontal' }, ctx).height }
    : naturalSize(child, { width: inner.width, height: 0, axis: 'vertical' }, ctx)));

  // Down a stack, height is the main axis: whatever is left over after the
  // natural heights is what the `fill` children actually asked for.
  if (!horizontal) {
    const fillers = kids.filter((k) => heightModeOf(k, 'vertical') === 'fill').length;
    if (fillers) {
      const naturalTotal = sizes.reduce((sum, sz) => sum + sz.height, 0);
      const spare = inner.height - naturalTotal - gap * Math.max(0, kids.length - 1);
      if (spare > 0) {
        const bonus = spare / fillers;
        kids.forEach((k, i) => {
          if (heightModeOf(k, 'vertical') === 'fill') sizes[i] = { ...sizes[i], height: sizes[i].height + bonus };
        });
      }
    }
  }
  const mains = sizes.map((s) => (horizontal ? s.width : s.height));
  const { start, gap: spacing } = distribute(l.distribute, horizontal ? inner.width : inner.height, mains, gap);

  let cursor = (horizontal ? inner.x : inner.y) + start;
  const children = kids.map((child, i) => {
    const size = sizes[i];
    const crossExtent = horizontal ? inner.height : inner.width;
    const crossSize = horizontal ? size.height : size.width;
    // `stretch` fills the cross axis; anything else aligns within it.
    // `stretch` fills the cross axis — but not for a node with a real
    // intrinsic width. An icon stretched to the container becomes a 1080px
    // ellipse where a mark belongs, which is what a real generation produced:
    // rows of purple blobs down a dental flyer. Such a node is centred at its
    // own size instead.
    const hugsCross = !horizontal && HUGS_WIDTH_ANY_AXIS.includes(child && child.type);
    const stretches = l.align === 'stretch' && !hugsCross;
    // A hugging node under `stretch` is centred; every other alignment is
    // honoured as written.
    const crossAlign = l.align === 'stretch' ? 'center' : l.align;
    const cross = stretches ? 0 : alignOffset(crossAlign, crossExtent, crossSize);
    const finalCross = stretches && !horizontal ? inner.width : (stretches && horizontal ? inner.height : crossSize);

    const childBox = horizontal
      ? { x: cursor, y: inner.y + cross, width: size.width, height: finalCross }
      : { x: inner.x + cross, y: cursor, width: finalCross, height: size.height };

    cursor += mains[i] + spacing;
    return place(child, childBox, ctx);
  });

  return reattach(children);
};

/**
 * Constraints resolution: re-anchor a resolved box for a different canvas size.
 * This is what makes a rendered document survive a variant size.
 *
 * @param {Object} box        the resolved geometry.
 * @param {Object} constraints { h, v }
 * @param {Object} from       original canvas { width, height }
 * @param {Object} to         target canvas { width, height }
 * @returns {Object} the re-anchored box.
 */
export const applyConstraints = (box, constraints = {}, from = {}, to = {}) => {
  const { h = 'left', v = 'top' } = isObj(constraints) ? constraints : {};
  const dx = (to.width || 0) - (from.width || 0);
  const dy = (to.height || 0) - (from.height || 0);
  const sx = from.width ? (to.width || 0) / from.width : 1;
  const sy = from.height ? (to.height || 0) / from.height : 1;
  const out = { ...box };

  switch (h) {
    case 'right': out.x = box.x + dx; break;
    case 'center': out.x = box.x + dx / 2; break;
    case 'stretch': out.width = box.width + dx; break;
    case 'scale': out.x = box.x * sx; out.width = box.width * sx; break;
    default: break; // left: unchanged
  }
  switch (v) {
    case 'bottom': out.y = box.y + dy; break;
    case 'center': out.y = box.y + dy / 2; break;
    case 'stretch': out.height = box.height + dy; break;
    case 'scale': out.y = box.y * sy; out.height = box.height * sy; break;
    default: break; // top: unchanged
  }
  return { x: round(out.x), y: round(out.y), width: round(out.width), height: round(out.height) };
};

/**
 * The width the longest unbreakable word in this copy needs.
 *
 * A word never wraps, so a column narrower than its longest word does not
 * "wrap" the text — it cuts the word in half. That is invisible to every
 * geometry check, because the element's BOX is still inside the page: only the
 * glyphs overflow. A real Sol flyer scored 100/100 while its headline rendered
 * as "Healt / smile / less."
 */
export const longestWordWidth = (text, style) => {
  const words = String(applyTextCase(text, style.case) || '').split(/\s+/).filter(Boolean);
  if (!words.length) return 0;
  const longest = words.reduce((max, w) => Math.max(max, w.length), 0);
  return longest * style.fontSize * advanceFactor(style);
};

export const __internals = { estimateTextHeight, fillFloor, rowWidths, longestWordWidth, wrappedLines, advanceFactor, measureContainer, resolvePadding, distribute, alignOffset, TYPE_SCALE };
